import type { SeedBlock } from "@/data/sections";

/**
 * Legal and policy pages.
 *
 * Every one of these seeds with `approvalState: "draft"`, and the page it
 * renders says so at the top. That is not a formality. Nothing below is legal
 * advice, nothing below has been adopted by HISCUS, and none of it is copied
 * from another organisation's policy. What it is, exactly, is a description of
 * what this website actually does, written so that it does not contradict the
 * software: if the wording and the code ever disagree, the wording is the bug.
 *
 * The approval state is what the client changes when a policy has really been
 * approved, and only then does the page drop the notice and show a review date.
 */

export type SeedPolicy = {
  slug: string;
  title: string;
  kind: "privacy" | "terms" | "safeguarding" | "cookies" | "donation" | "media-consent" | "other";
  summary: string;
  statusNote: string;
  contactRoute: string;
  order: number;
  body: SeedBlock[];
  source: string;
};

export const seedPolicies: SeedPolicy[] = [
  {
    slug: "privacy",
    title: "Privacy",
    kind: "privacy",
    summary:
      "What this website collects, what it does not collect, and the status of the formal HISCUS privacy policy.",
    statusNote:
      "HISCUS's formal privacy policy is being finalised. The description below is accurate for this website today, and is not a substitute for the approved policy.",
    contactRoute: "/contact",
    order: 0,
    source: "Written from the behaviour of this codebase: server/src/routes/enquiry.ts and client/src/components/forms/EnquiryForm.tsx.",
    body: [
      { kind: "heading", level: 2, text: "What this site collects" },
      {
        kind: "paragraph",
        text: "The enquiry form collects the name, email address and message you enter, plus the optional phone number, country and organisation, and the enquiry type you select. It is sent to HISCUS so that someone can reply to you, and a copy is stored so that a message is not lost if delivery fails.",
      },
      {
        kind: "paragraph",
        text: "Your consent checkbox is required before the form will submit. The form will not display a success message unless the message was actually delivered.",
      },
      { kind: "heading", level: 2, text: "What this site does not do" },
      {
        kind: "list",
        items: [
          "This website's own code loads no advertising or tracking scripts.",
          "No cookies are set for analytics or marketing purposes.",
          "No personal data is placed in URLs or query strings.",
          "Fonts and images are served from this site's own domain rather than a third-party CDN.",
          "Video is not loaded until you choose to play it, so no request reaches a video host on page load.",
        ],
      },
      { kind: "heading", level: 2, text: "Cookies this site does set" },
      {
        kind: "paragraph",
        text: "Two, and only for staff. Signing in to the content administration area sets a session cookie and a second cookie used to verify that a request came from the administration screen rather than from another website. Both are removed when a member of staff signs out. A visitor who never signs in is never given either.",
      },
      { kind: "heading", level: 2, text: "Abuse prevention" },
      {
        kind: "paragraph",
        text: "Security measures are used to limit automated and repeated submissions. Connection information may be used to protect the enquiry service from abuse.",
      },
      { kind: "heading", level: 2, text: "Retention and requests" },
      {
        kind: "paragraph",
        text: "Retention periods and the route for requesting deletion of your enquiry will be confirmed in the approved policy. In the meantime, you can ask HISCUS to delete an enquiry you have sent by getting in touch.",
      },
    ],
  },

  {
    slug: "terms",
    title: "Terms and conditions",
    kind: "terms",
    summary: "The terms on which this website is provided, and the limits of what it offers.",
    statusNote:
      "These terms are a draft prepared for HISCUS to review and adopt. They have not been approved, and they are not legal advice. Until HISCUS approves them, they describe how this website is operated rather than forming an agreement.",
    contactRoute: "/contact",
    order: 1,
    source:
      "Written from the behaviour of this website. No wording is taken from another organisation's terms.",
    body: [
      { kind: "heading", level: 2, text: "Who operates this website" },
      {
        kind: "paragraph",
        text: "This website is operated by Himshikhara Socio-Cultural Society (HISCUS Nepal), a nonprofit registered in Nepal with the District Administration Office and affiliated to the Social Welfare Council. Registration identifiers are published on the Transparency page.",
      },
      { kind: "heading", level: 2, text: "What this website is for" },
      {
        kind: "paragraph",
        text: "It describes the organisation's programmes and publishes reporting about them. It is an information and contact website. It does not take payments, does not process donations, and does not create a contract for any placement, trek, partnership or service.",
      },
      { kind: "heading", level: 2, text: "Enquiries" },
      {
        kind: "paragraph",
        text: "Sending an enquiry does not commit either side to anything. Volunteering placements, partnerships, sponsorships and Charity Trek departures are agreed separately and in writing, and the terms that apply to them are the terms in that written agreement, not these.",
      },
      { kind: "heading", level: 2, text: "Accuracy of what is published" },
      {
        kind: "paragraph",
        text: "Figures on this site carry the reporting period they refer to. Where a detail has not been published by HISCUS, this site leaves it out rather than estimating it. If you find something on these pages that is wrong or out of date, tell us and it will be corrected.",
      },
      { kind: "heading", level: 2, text: "Links to other websites" },
      {
        kind: "paragraph",
        text: "Some links leave this website, including the Charity Trek link to Mountain Routes, which is operated by Mountain Routes and not by HISCUS. HISCUS does not control those websites and is not responsible for their content, their terms or their handling of your data.",
      },
      { kind: "heading", level: 2, text: "Photographs and text" },
      {
        kind: "paragraph",
        text: "The photographs on this site document HISCUS programmes. Please contact HISCUS before reproducing photographs or substantial passages of text.",
      },
      { kind: "heading", level: 2, text: "Safeguarding and images of children" },
      {
        kind: "paragraph",
        text: "Photographs containing identifiable children are published only where consent for public use has been recorded. If you are shown in a photograph on this site and want it removed, contact HISCUS and it will be taken down while the request is considered.",
      },
      { kind: "heading", level: 2, text: "Availability" },
      {
        kind: "paragraph",
        text: "This website is provided as it is. HISCUS does not guarantee that it will be available without interruption, and may change or remove pages as its work changes.",
      },
      { kind: "heading", level: 2, text: "Governing law" },
      {
        kind: "callout",
        tone: "warning",
        text: "Additional policy information will be published following organizational approval.",
      },
      { kind: "heading", level: 2, text: "Contact" },
      {
        kind: "paragraph",
        text: "Questions about these terms, or about anything published here, can be sent through the enquiry form.",
      },
    ],
  },

  {
    slug: "safeguarding",
    title: "Safeguarding",
    kind: "safeguarding",
    summary:
      "How HISCUS handles images, consent and concerns, and the status of the formal safeguarding policy.",
    statusNote:
      "HISCUS's formal safeguarding policy is being prepared. What follows describes current practice on this website and in its media handling. It is not an approved policy, and it is not a substitute for one.",
    contactRoute: "/contact",
    order: 2,
    source: "Written from the behaviour of this codebase and the project's photography rules.",
    body: [
      { kind: "heading", level: 2, text: "Images of children" },
      {
        kind: "paragraph",
        text: "Photographs containing identifiable children are published only after consent for public use has been recorded.",
      },
      { kind: "heading", level: 2, text: "How people are described" },
      {
        kind: "paragraph",
        text: "Participants are described as participants. No personal history, medical detail or family circumstance is published about an identifiable individual. Captions describe what is visible in the frame and nothing more; where the project, place, date or photographer has not been published, the field is left empty rather than guessed.",
      },
      { kind: "heading", level: 2, text: "Raising a concern" },
      {
        kind: "callout",
        tone: "warning",
        text: "A direct safeguarding contact has not yet been published. Until HISCUS supplies a named person or role and a way to reach them, concerns can be sent through the enquiry form, which reaches the team.",
      },
      { kind: "heading", level: 2, text: "Asking for a photograph to be removed" },
      {
        kind: "paragraph",
        text: "If you appear in a photograph on this site, or a child in your care does, and you want it removed, say so through the enquiry form. It will be taken off the site while the request is considered.",
      },
    ],
  },

  {
    slug: "cookies",
    title: "Cookies and analytics",
    kind: "cookies",
    summary: "Every cookie this website sets, and why.",
    statusNote:
      "This page describes what the website does today. It is maintained alongside the code rather than approved as a policy.",
    contactRoute: "/privacy",
    order: 3,
    source: "server/src/middleware/auth.ts and client/src/components/forms/EnquiryForm.tsx.",
    body: [
      { kind: "heading", level: 2, text: "Cookies for visitors" },
      {
        kind: "paragraph",
        text: "None. Reading this site, browsing the gallery, filling in the enquiry form and playing a video all happen without this website setting a cookie.",
      },
      { kind: "heading", level: 2, text: "Cookies for staff" },
      {
        kind: "factList",
        facts: [
          { label: "hiscus_admin", value: "Signed session for the content administration area. Expires after eight hours, or at sign-out." },
          { label: "hiscus_csrf", value: "Confirms that an administration request came from the administration screen. Same lifetime." },
        ],
      },
      { kind: "heading", level: 2, text: "Analytics" },
      {
        kind: "paragraph",
        text: "This website's own code contains no analytics. Cloudflare, which serves the site, currently injects its own analytics script as pages are delivered; that is a hosting setting rather than part of this website, HISCUS does not receive the data, and it is in the process of being turned off.",
      },
      { kind: "heading", level: 2, text: "Video" },
      {
        kind: "paragraph",
        text: "Where a video is embedded, it is not loaded until you click to play it. Until then no request goes to the video host, so the host sets nothing and sees nothing.",
      },
    ],
  },
];
